import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Shield } from "lucide-react";
import { PlanCheckoutButton } from "@/components/PlanCheckoutButton";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Logo } from "@/components/Logo";

const Pricing = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <MegaMenuNav />

      {/* Pricing Content */}
      <div className="container mx-auto px-4 py-20">
        <div className="mb-16 text-center">
          <h1 className="mb-4 text-5xl font-bold">Choose Your Plan</h1>
          <p className="text-xl text-muted-foreground">ATS-Optimized Resumes & Cover Letters That Get Interviews</p>
        </div>

        <div className="mx-auto grid max-w-6xl gap-8 md:grid-cols-3">
          {/* FREE */}
          <Card className="p-8">
            <div className="mb-6">
              <h3 className="mb-2 text-2xl font-bold">Free</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold">$0</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground">Perfect for casual job seekers</p>
            </div>
            <div className="mb-6">
              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <p className="text-2xl font-bold text-primary">10 credits</p>
                <p className="text-xs text-muted-foreground">per month</p>
              </div>
              <p className="text-xs text-center mt-2 text-muted-foreground">1 credit per resume/letter</p>
            </div>
            <ul className="mb-8 space-y-3">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">10 AI generations per month</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Create Resume & Cover Letter</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Application Tracker</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Document Management</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">PDF & Word export</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">ATS-optimized formatting</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Job Search (1 credit per page)</span>
              </li>
            </ul>
            <Link to="/signup">
              <Button className="w-full" variant="outline">
                Get Started Free
              </Button>
            </Link>
          </Card>

          {/* PRO */}
          <Card className="border-2 border-primary p-8 relative">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">
              MOST POPULAR
            </Badge>
            <div className="mb-6">
              <h3 className="mb-2 text-2xl font-bold">Pro</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold">$29</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground">Best for active job seekers</p>
            </div>
            <div className="mb-6">
              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <p className="text-2xl font-bold text-primary">100 credits</p>
                <p className="text-xs text-muted-foreground">per month</p>
              </div>
              <p className="text-xs text-center mt-2 text-muted-foreground">Rollover up to 50 credits</p>
            </div>
            <ul className="mb-8 space-y-3">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm"><strong>Everything in Free, plus:</strong></span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">100 credits per month</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">ATS Score Checker (2 credits)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">LinkedIn Optimizer (2 credits)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Job Analyzer (2 credits)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Salary Negotiation Analysis</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Up to 50 credits rollover</span>
              </li>
            </ul>
            <PlanCheckoutButton plan="pro" className="w-full">
              Start Pro
            </PlanCheckoutButton>
          </Card>

          {/* PREMIUM */}
          <Card className="p-8 border-2 border-accent">
            <div className="mb-6">
              <h3 className="mb-2 text-2xl font-bold">Premium</h3>
              <div className="mb-4">
                <span className="text-5xl font-bold">$79</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground">For serious professionals</p>
            </div>
            <div className="mb-6">
              <div className="text-center p-4 bg-accent/10 rounded-lg">
                <p className="text-2xl font-bold text-accent">200 credits</p>
                <p className="text-xs text-muted-foreground">per month</p>
              </div>
              <p className="text-xs text-center mt-2 text-accent font-semibold">Unlimited 1-2 credit actions + Up to 100 rollover</p>
            </div>
            <ul className="mb-8 space-y-3">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm"><strong>Everything in Pro, plus:</strong></span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">200 credits per month</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-accent" />
                <span className="text-sm"><strong>✨ Unlimited 1-2 credit actions</strong></span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Smart Apply (5 credits)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Company Insights (5 credits)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">AI Interview Prep (5 credits)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Full Salary Strategy (5 credits)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm">Up to 100 credits rollover</span>
              </li>
            </ul>
            <PlanCheckoutButton plan="premium" className="w-full">
              Go Premium
            </PlanCheckoutButton>
          </Card>
        </div>

        {/* Comparison Table */}
        <div className="mx-auto mt-16 max-w-6xl">
          <h2 className="mb-8 text-center text-3xl font-bold">Feature Comparison</h2>
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-4 text-left font-semibold">Feature</th>
                    <th className="p-4 text-center font-semibold">FREE</th>
                    <th className="p-4 text-center font-semibold">PRO</th>
                    <th className="p-4 text-center font-semibold">PREMIUM</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="p-4">Credits per Month</td>
                    <td className="p-4 text-center">10</td>
                    <td className="p-4 text-center">100</td>
                    <td className="p-4 text-center">200</td>
                  </tr>
                  <tr>
                    <td className="p-4">Credit Rollover</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">Up to 50</td>
                    <td className="p-4 text-center">Up to 100</td>
                  </tr>
                  <tr>
                    <td className="p-4">Unlimited 1-2 Credit Actions</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">Active Documents</td>
                    <td className="p-4 text-center">1+1</td>
                    <td className="p-4 text-center">5+5</td>
                    <td className="p-4 text-center">Unlimited</td>
                  </tr>
                  <tr>
                    <td className="p-4">All Templates</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">ATS Optimization</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">PDF & Word Export</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">ATS Score Checker</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">LinkedIn Optimizer</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">Job Analyzer</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">Job Search (1M+ jobs)</td>
                    <td className="p-4 text-center">✓ (1 credit/page)</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">Salary Negotiation Tools</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">Smart Apply</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">Company Insights</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">AI Interview Prep</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">Smart Apply (100+ jobs)</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                  <tr>
                    <td className="p-4">Priority Support</td>
                    <td className="p-4 text-center">✗</td>
                    <td className="p-4 text-center">✓</td>
                    <td className="p-4 text-center">✓</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* FAQ Section */}
        <div className="mx-auto mt-20 max-w-3xl">
          <h2 className="mb-8 text-center text-3xl font-bold">Common Questions</h2>
          <div className="space-y-6">
            {[
              {
                q: "What counts as a \"credit\"?",
                a: "One credit is used each time you generate a new AI-powered resume or cover letter. Job Search uses 1 credit per page of 10 results. Minor edits, template changes, and exports don't use credits."
              },
              {
                q: "How do credits work for Job Search?",
                a: "Job Search is available on Free tier with metering. Each page of 10 job results costs 1 credit. Clicking 'Next' loads another 10 jobs and uses 1 more credit. Refreshing the same page within 10 minutes won't charge again (cached results)."
              },
              {
                q: "Can I use credits for both resumes AND cover letters?",
                a: "Yes! Credits work for any combination. With 10 credits on Free, you could create 10 resumes, 10 cover letters, search 10 pages of jobs, or any mix like 5 resumes + 3 letters + 2 job searches."
              },
              {
                q: "Why was I charged again for the same job search?",
                a: "You won't be charged twice for the same search page within 10 minutes (we cache results). However, changing your query, location, or moving to a different page will use another credit as these are new searches."
              },
              {
                q: "Do credits roll over each month?",
                a: "No, credits reset at the start of each billing cycle and don't roll over."
              },
              {
                q: "What makes these resumes ATS-compatible?",
                a: "Our resumes use ATS-friendly formatting: standard fonts (Arial/Calibri), simple layouts, proper section headers, and parseable text. This helps you get past automated screening systems."
              },
              {
                q: "Can I cancel anytime?",
                a: "Yes! All plans are month-to-month with no annual commitment."
              },
              {
                q: "What if I run out of credits?",
                a: "You can still access and export existing documents. To create new ones, wait until credits reset or upgrade to a higher plan."
              },
              {
                q: "Is my payment secure?",
                a: "Yes! We use Stripe for secure payment processing. Your payment information is encrypted and never stored on our servers."
              }
            ].map((faq, i) => (
              <Card key={i} className="p-6">
                <h3 className="mb-2 text-lg font-semibold">{faq.q}</h3>
                <p className="text-muted-foreground">{faq.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t bg-muted/30 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <Link to="/" className="flex items-center gap-2">
              <Logo className="h-12 w-12" />
              <span className="font-semibold">LoopBaan.ai</span>
            </Link>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="/contact" className="hover:text-foreground">Contact</Link>
              <Link to="/ats-optimization" className="hover:text-foreground">ATS Guide</Link>
              <Link to="/terms" className="hover:text-foreground">Terms</Link>
              <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            </div>
          </div>
          <div className="mt-8 text-center text-sm text-muted-foreground">
            © 2025 LoopBaan.ai. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Pricing;